import type { MouseEvent } from 'react';
import type { MenuItem, TopLevelMenuItem } from '../types';
import type { NavigateFunction } from 'react-router';
import isFeatureEnabled from '~/components/common/is-feature-enabled';
import { hasUserPermissions } from '~/elc/features/permissions';
import {
  learnerCanAccessGo1,
  learnerCanAccessLinkedInLearning,
  learnerCanAccessUdemy,
} from '~/features/learner-content-provider-development/utils';
import { APP_CONSTANTS, getCurrentUser, I18n } from '~/common/explicit-exports';
import { LEARNER_ROUTES } from '../constants';

const handleCompanyNavigate = (
  navigate: NavigateFunction | undefined,
  event: MouseEvent<HTMLAnchorElement>,
  href: string,
) => {
  if (navigate) {
    event.preventDefault();
    navigate(href);
  }
};

export function generateSidebarItems(
  assignmentsCount: number,
  navigate: NavigateFunction | undefined,
): TopLevelMenuItem[] {
  const TRANSLATED_HOME = I18n.t('js.learner.home.sidebar.home');
  const TRANSLATED_EVENTS = I18n.t('common.content.event', { count: 2 });
  const TRANSLATED_DEVELOPMENT = I18n.t('js.learner.home.sidebar.development');
  const TRANSLATED_LIBRARIES = I18n.t('common.content.university', { count: 2 });
  const TRANSLATED_REVIEW = I18n.t('js.learner.home.sidebar.review');
  const TRANSLATED_COMPANY = I18n.t('js.learner.home.sidebar.company');
  const TRANSLATED_DIRECTORY = I18n.t('js.learner.home.sidebar.directory');
  const TRANSLATED_LEADERBOARDS = I18n.t('js.learner.home.sidebar.leaderboards');
  const TRANSLATED_MANAGER_DASHBOARD = I18n.t('js.learner.home.sidebar.manager_dashboard');

  const { enterprise, isInternal } = getCurrentUser();

  // Show/hide company tab items
  const showCompanyDirectory = isInternal && enterprise.isLearnerCompanyDirectoryEnabled;
  const showCompanyLeaderboards =
    isFeatureEnabled('fy23_q4_leaderboards') && isInternal && enterprise.isLearnerCompanyLeaderboardsEnabled;

  const showGo1 = learnerCanAccessGo1();
  const showUdemy = learnerCanAccessUdemy();
  const showLinkedInLearning = learnerCanAccessLinkedInLearning();
  const showReviewTab = assignmentsCount > 0;
  const showDevelopmentTab = showUdemy || showLinkedInLearning || showGo1;
  const showCompanyTab = showCompanyDirectory || showCompanyLeaderboards || showReviewTab;

  const companyTabSubitems: MenuItem[] = showReviewTab
    ? [
        {
          text: TRANSLATED_REVIEW,
          href: LEARNER_ROUTES.review,
        },
      ]
    : [];

  const companyTabItem: TopLevelMenuItem = {
    text: TRANSLATED_COMPANY,
    inactiveIconName: 'building:regular',
    activeIconName: 'building:solid',
    href: LEARNER_ROUTES.review,
    subitems: companyTabSubitems,
  };

  const addSubitem = (text: string, href: string) => {
    companyTabSubitems.unshift({
      text,
      href,
      onClick: (event) => handleCompanyNavigate(navigate, event, href),
    });
  };

  if (showCompanyDirectory) {
    addSubitem(TRANSLATED_DIRECTORY, LEARNER_ROUTES.companyDirectory);
    companyTabItem.href = LEARNER_ROUTES.companyDirectory;
    companyTabItem.onClick = (event) => handleCompanyNavigate(navigate, event, LEARNER_ROUTES.companyDirectory);
  }

  if (showCompanyLeaderboards) {
    addSubitem(TRANSLATED_LEADERBOARDS, LEARNER_ROUTES.companyLeaderboards);
    if (!showCompanyDirectory) {
      companyTabItem.href = LEARNER_ROUTES.companyLeaderboards;
      companyTabItem.onClick = (event) => handleCompanyNavigate(navigate, event, LEARNER_ROUTES.companyLeaderboards);
    }
  }

  const navItems: TopLevelMenuItem[] = [];

  navItems.push({
    text: TRANSLATED_HOME,
    href: LEARNER_ROUTES.home,
    inactiveIconName: 'home:regular',
    activeIconName: 'home:solid',
  });

  navItems.push({
    text: TRANSLATED_LIBRARIES,
    href: LEARNER_ROUTES.libraries,
    inactiveIconName: 'library:regular',
    activeIconName: 'library:solid',
    rootActivePath: LEARNER_ROUTES.libraries,
  });

  navItems.push({
    text: TRANSLATED_EVENTS,
    href: LEARNER_ROUTES.events,
    inactiveIconName: 'event:regular',
    activeIconName: 'event:solid',
  });

  if (showDevelopmentTab) {
    let developmentSubItems: MenuItem[] = null;

    const hasMultipleSourcesEnabled = [showGo1, showUdemy, showLinkedInLearning].filter(Boolean).length > 1;
    if (hasMultipleSourcesEnabled) {
      developmentSubItems = [];

      if (showGo1) {
        developmentSubItems.push({
          text: I18n.t('js.learner.home.sidebar.development_sources.go1'),
          href: LEARNER_ROUTES.development,
        });
      }

      if (showLinkedInLearning) {
        developmentSubItems.push({
          text: I18n.t('js.learner.home.sidebar.development_sources.linkedin_learning'),
          href: LEARNER_ROUTES.developmentLinkedInLearning,
        });
      }

      if (showUdemy) {
        developmentSubItems.push({
          text: I18n.t('js.learner.home.sidebar.development_sources.udemy'),
          href: LEARNER_ROUTES.developmentUdemy,
        });
      }
    }

    let rootActivePath;
    if (showGo1) {
      rootActivePath = LEARNER_ROUTES.development;
    } else if (showLinkedInLearning) {
      rootActivePath = LEARNER_ROUTES.developmentLinkedInLearning;
    } else if (showUdemy) {
      rootActivePath = LEARNER_ROUTES.developmentUdemy;
    }

    navItems.push({
      text: TRANSLATED_DEVELOPMENT,
      href: rootActivePath,
      inactiveIconName: 'stairs:regular',
      activeIconName: 'stairs:solid',
      rootActivePath,
      subitems: developmentSubItems,
    });
  }

  if (hasUserPermissions([APP_CONSTANTS.PERMISSIONS.MANAGER])) {
    navItems.push({
      text: TRANSLATED_MANAGER_DASHBOARD,
      href: LEARNER_ROUTES.managerDashboardTeam,
      inactiveIconName: 'graph-pie:regular',
      activeIconName: 'graph-pie:solid',
      rootActivePath: LEARNER_ROUTES.managerDashboard,
    });
  }

  if (showCompanyTab) {
    navItems.push(companyTabItem);
  }

  return navItems;
}
